import React, { useState, useRef, useEffect, memo, useMemo } from 'react';
import { Code, BookOpen, Copy, Check, Maximize2, Minimize2 } from 'lucide-react';
import { AlgorithmMeta } from '../../../core/registry/algorithmRegistry';
import { getAlgorithmPseudocodeFile, CODE_LANGUAGES } from '../../../core/utils/codeGenerator';

interface CodeViewerProps {
  algorithm: AlgorithmMeta | null;
  codeContent: string;
  selectedLanguage: string;
  onLanguageChange: (lang: string) => void;
  activeLine?: number;
}

export const CodeViewer: React.FC<CodeViewerProps> = memo(({
  algorithm,
  codeContent,
  selectedLanguage,
  onLanguageChange,
  activeLine,
}) => {
  const [activeTab, setActiveTab] = useState<'readme' | 'code'>('readme');
  const [copied, setCopied]       = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const activeLineRef             = useRef<HTMLDivElement>(null);

  useEffect(() => {
    activeLineRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [activeLine]);

  const pseudocode = useMemo(() => {
    if (!algorithm) return '';
    const raw = getAlgorithmPseudocodeFile(algorithm.id, algorithm.name);
    return typeof raw === 'string' ? raw : 'Loading pseudocode…';
  }, [algorithm]);

  const safeCode = useMemo(() => {
    return typeof codeContent === 'string' ? codeContent : 'Loading implementation…';
  }, [codeContent]);

  const pseudocodeLines = useMemo(() => pseudocode.split('\n'), [pseudocode]);

  if (!algorithm) {
    return (
      <div
        className="flex-1 flex flex-col items-center justify-center min-h-0 gap-3"
        style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}
      >
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center"
          style={{
            background: 'rgba(255,255,255,0.03)',
            border: '1px solid rgba(255,255,255,0.06)',
          }}
        >
          <Code className="w-4 h-4" style={{ color: '#3f3f46' }} />
        </div>
        <p className="text-[11px] font-mono" style={{ color: '#3f3f46' }}>No algorithm selected</p>
      </div>
    );
  }

  const handleCopy = async () => {
    try {
      const text = activeTab === 'code'
        ? safeCode
        : `${algorithm.name}\n\n${algorithm.description}\n\n${pseudocode}`;
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.error('[CodeViewer] Copy failed:', err);
    }
  };

  return (
    <div
      className="flex-1 flex flex-col min-h-0 overflow-hidden"
      style={{
        borderBottom: '1px solid rgba(255,255,255,0.06)',
        ...(isExpanded ? { position: 'fixed' as const, inset: 0, zIndex: 60, background: '#0a0a0b', padding: '16px', overflow: 'hidden' } : {}),
      }}
    >
      {/* ── Tab header ── */}
      <div
        className="flex items-center gap-1 px-3 py-2 flex-shrink-0"
        style={{
          background: 'rgba(255,255,255,0.02)',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
        }}
      >
        {/* Pseudocode tab */}
        <button
          onClick={() => setActiveTab('readme')}
          className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-all duration-200"
          style={{
            background: activeTab === 'readme' ? 'rgba(59,130,246,0.12)' : 'transparent',
            border:     activeTab === 'readme' ? '1px solid rgba(59,130,246,0.2)' : '1px solid transparent',
            color:      activeTab === 'readme' ? '#60a5fa' : '#52525b',
          }}
        >
          <BookOpen className="w-3 h-3 flex-shrink-0" />
          Pseudocode
        </button>

        {/* Implementation tab */}
        <button
          onClick={() => setActiveTab('code')}
          className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-all duration-200"
          style={{
            background: activeTab === 'code' ? 'rgba(59,130,246,0.12)' : 'transparent',
            border:     activeTab === 'code' ? '1px solid rgba(59,130,246,0.2)' : '1px solid transparent',
            color:      activeTab === 'code' ? '#60a5fa' : '#52525b',
          }}
        >
          <Code className="w-3 h-3 flex-shrink-0" />
          Implementation
        </button>

        {/* Copy */}
        <button
          type="button"
          onClick={handleCopy}
          title="Copy to clipboard"
          className="w-7 h-7 rounded-md flex items-center justify-center transition-all duration-200 flex-shrink-0"
          style={{
            background: 'rgba(255,255,255,0.04)',
            border: '1px solid rgba(255,255,255,0.07)',
            color: copied ? '#10b981' : '#52525b',
          }}
        >
          {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
        </button>

        {/* Expand / Collapse */}
        <button
          type="button"
          onClick={() => setIsExpanded(prev => !prev)}
          title={isExpanded ? 'Collapse code viewer' : 'Expand code viewer'}
          className="w-7 h-7 rounded-md flex items-center justify-center transition-all duration-200 flex-shrink-0"
          style={{
            background: isExpanded ? 'rgba(59,130,246,0.12)' : 'rgba(255,255,255,0.04)',
            border: isExpanded ? '1px solid rgba(59,130,246,0.2)' : '1px solid rgba(255,255,255,0.07)',
            color: isExpanded ? '#60a5fa' : '#52525b',
          }}
        >
          {isExpanded ? <Minimize2 className="w-3.5 h-3.5" /> : <Maximize2 className="w-3.5 h-3.5" />}
        </button>
      </div>

      {/* ── Language selector (code tab) ── */}
      {activeTab === 'code' && (
        <div
          className="flex items-center gap-2 px-3 py-1.5 flex-shrink-0"
          style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}
        >
          <select
            value={selectedLanguage}
            onChange={e => onLanguageChange(e.target.value)}
            className="flex-1 text-xs font-mono text-zinc-300 rounded-md px-2 py-1 focus:outline-none transition-all duration-200"
            style={{
              background: 'rgba(255,255,255,0.04)',
              border: '1px solid rgba(255,255,255,0.07)',
            }}
          >
            {CODE_LANGUAGES.map(lang => (
              <option key={lang} value={lang} style={{ background: '#18181b' }}>
                {lang}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* ── Content ── */}
      <div
        className="flex-1 overflow-auto"
        style={{
          minHeight: isExpanded ? '60vh' : '280px',
        }}
      >
        {activeTab === 'readme' ? (
          <div className="p-3">
            <div className="mb-3">
              <h3 className="text-[12px] font-semibold text-zinc-200 tracking-tight leading-none">
                {algorithm.name}
              </h3>
              <p className="text-[11px] text-zinc-600 mt-1.5 leading-relaxed">
                {algorithm.description}
              </p>
            </div>
            <div
              className="rounded-lg overflow-hidden"
              style={{
                background: 'rgba(0,0,0,0.28)',
                border: '1px solid rgba(255,255,255,0.06)',
              }}
            >
              {pseudocodeLines.map((line, index) => {
                const lineNumber = index + 1;
                const isActive   = activeLine === lineNumber;
                return (
                  <div
                    key={`ps-${lineNumber}`}
                    ref={isActive ? activeLineRef : null}
                    className={`code-line${isActive ? ' code-line-active-amber' : ''}`}
                  >
                    <span
                      className="w-8 flex-shrink-0 text-right pr-3 py-0.5 pl-1 text-[10px] font-mono tabular-nums select-none"
                      style={{ color: isActive ? '#d97706' : '#3f3f46' }}
                    >
                      {lineNumber}
                    </span>
                    <span
                      className="flex-1 py-0.5 pr-3 text-[11px] font-mono whitespace-pre-wrap leading-5"
                      style={{ color: isActive ? '#fde68a' : '#71717a' }}
                    >
                      {line || ' '}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="p-3">
            <div
              className="rounded-lg overflow-hidden"
              style={{
                background: 'rgba(0,0,0,0.28)',
                border: '1px solid rgba(255,255,255,0.06)',
              }}
            >
              {safeCode.split('\n').map((line, index) => {
                const lineNumber = index + 1;
                const isActive   = activeLine === lineNumber;
                return (
                  <div
                    key={`cd-${lineNumber}`}
                    ref={isActive ? activeLineRef : null}
                    className={`code-line${isActive ? ' code-line-active-blue' : ''}`}
                  >
                    <span
                      className="w-8 flex-shrink-0 text-right pr-3 py-0.5 pl-1 text-[10px] font-mono tabular-nums select-none"
                      style={{ color: isActive ? '#60a5fa' : '#3f3f46' }}
                    >
                      {lineNumber}
                    </span>
                    <span
                      className="flex-1 py-0.5 pr-3 text-[11px] font-mono whitespace-pre leading-5"
                      style={{ color: isActive ? '#bfdbfe' : '#71717a' }}
                    >
                      {line || ' '}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
});
